/**
 * Ground-projected cursor marker for armed point/point-charge abilities.
 *
 * Shows a small center disc at the raycast-hit point + an AoE ring at the
 * ability's effect radius (when one exists — Plant Tree's `aoe-damage`
 * primitive carries a `radius` param). Hidden when the cast cursor is idle.
 *
 * Architecture: module is render-only and reads world position through
 * `hover(point)`. Main.ts owns the raycast (already has the camera + terrain
 * + sampleHeight pipeline for the existing placement-preview); this module
 * just turns the resolved point into a moved mesh.
 *
 * Spec: docs/design/09-abilities.md §"Targeting modes" — point + point-charge
 *       modes both want a ground marker; unit mode is target-highlight (TODO).
 */
import * as THREE from "three";
import type { AbilityId } from "@tribez/shared";
import { ABILITIES } from "@tribez/shared";

export interface AbilityTargetMarker {
  /** Begin showing the marker for an armed ability. Reads AoE radius from registry. */
  show(abilityId: AbilityId): void;
  /** Stop showing. */
  hide(): void;
  /** Update the marker's world position. `null` = mouse off-terrain → marker hidden until next valid hover. */
  hover(point: { x: number; y: number; z: number } | null): void;
}

/**
 * Walks `def.effects[*].params.radius` and returns the largest positive
 * radius found. 0 means the ability has no AoE — no outer ring rendered.
 */
function readAoeRadius(abilityId: AbilityId): number {
  const def = ABILITIES[abilityId];
  if (!def) return 0;
  let best = 0;
  for (const eff of def.effects) {
    const r = eff.params?.radius;
    if (typeof r === "number" && r > best) best = r;
  }
  return best;
}

export function createAbilityTargetMarker(scene: THREE.Scene): AbilityTargetMarker {
  const group = new THREE.Group();
  group.name = "ability-target-marker";
  group.visible = false;

  // Center dot — small green disc directly at the cursor's terrain hit.
  const dotGeo = new THREE.CircleGeometry(0.45, 32);
  const dotMat = new THREE.MeshBasicMaterial({
    color: 0x88ffaa,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide,
    depthWrite: false,
    depthTest: false,
  });
  const dot = new THREE.Mesh(dotGeo, dotMat);
  dot.rotation.x = -Math.PI / 2;
  dot.renderOrder = 1000;
  group.add(dot);

  // AoE outer ring — rebuilt per-show to match the active ability's radius.
  let aoeRing: THREE.Mesh | null = null;
  function rebuildAoeRing(radius: number): void {
    if (aoeRing) {
      group.remove(aoeRing);
      aoeRing.geometry.dispose();
      (aoeRing.material as THREE.Material).dispose();
      aoeRing = null;
    }
    if (radius <= 0) return;
    const innerR = Math.max(0, radius - 0.18);
    const outerR = radius;
    const geo = new THREE.RingGeometry(innerR, outerR, 64);
    const mat = new THREE.MeshBasicMaterial({
      color: 0x66ff88,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: false,
    });
    aoeRing = new THREE.Mesh(geo, mat);
    aoeRing.rotation.x = -Math.PI / 2;
    aoeRing.renderOrder = 1000;
    group.add(aoeRing);
  }

  scene.add(group);

  // `armed` gates `hover` so we don't render anything when the cursor goes
  // idle even if a stale point flows in. `hide()` clobbers visibility
  // immediately; `show()` defers visibility to the next `hover` call.
  let armed = false;

  return {
    show(abilityId) {
      armed = true;
      rebuildAoeRing(readAoeRadius(abilityId));
    },
    hide() {
      armed = false;
      group.visible = false;
    },
    hover(point) {
      if (!armed) return;
      if (!point) {
        group.visible = false;
        return;
      }
      group.visible = true;
      // Lift slightly off the terrain to dodge z-fight at glancing angles.
      group.position.set(point.x, point.y + 0.05, point.z);
    },
  };
}
